import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";

/** `turn.prompt` — what the human asked, and the one tinted, right-aligned
 * shape in a transcript. Everything the agent does renders flat and
 * left-aligned, so a reader scanning a long session finds every turn
 * boundary by looking down the right edge alone.
 *
 * The component owns the row, not just the bubble: alignment IS the shape,
 * so a caller cannot accidentally left-align a prompt and quietly destroy
 * the scanning property. */
@component({ name: "std-turn-prompt" })
export class TurnPrompt extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");

  static makeWith(props: TurnPrompt.Props): TurnPrompt {
    return (TurnPrompt.make() as TurnPrompt).bind(props);
  }

  bind(props: TurnPrompt.Props): this {
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      :host {
        display: flex;
        /* Right-alignment IS the shape, and it can only happen against a
           full column — in a shrink-to-fit parent the host would hug the
           bubble and the alignment would silently do nothing. */
        width: 100%;
        justify-content: flex-end;
      }
      #bubble {
        max-width: var(--prompt-max-width, 78%);
        padding: 10px 16px;
        border-radius: 18px;
        background: var(
          --fill-sec,
          color-mix(in srgb, var(--text-pri, #1f2328) 7%, transparent)
        );
      }
      /* The reading register: what the user typed is read, not scanned,
         and it sits at the same size as the reply it provoked. */
      #text {
        margin: 0;
        --font-size: var(--p-size);
        --line-height-ratio: 1.6;
        color: var(--text-pri, #1f2328);
        white-space: pre-wrap;
        overflow-wrap: anywhere;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="bubble">
      <p id="text"></p>
    </div>
  `;
}

export namespace TurnPrompt {
  export type Props = {
    text: string;
  };
}
